<template>
  <ui-component-modal
    :modalTitle="'Add options'"
    modalSize="extra-large"
    :onClickCancel="onClickCancel"
    :onClickSave="saveReservation"
    :showModal="showModal"
    :isSaving="isSaving"
    :isSavingError="isSavingError"
    :savingErrorMessage="'Something went wrong!'"
    :hideFooterButtons="isSavingError"
    :disableSaveButton="newOptions.length === 0"
    @closeModal="onClickCancel"
  >
    <template slot="modalTitle"></template>
    <template v-slot:content>
      <div class="columns">
        <div class="column">
          <div class="tabs is-boxed">
            <ul>
              <template v-for="(category, index) in filteredCategories">
                <li
                  :key="index"
                  v-if="category.NrOfOptions > 0"
                  @click="changeCategory(category.Id)"
                  :class="{ 'is-active': selectedCategory === category.Id }"
                >
                  <a>{{ getCategoryName(category.Id) }}</a>
                </li>
              </template>
            </ul>
          </div>

          <div>
            <ui-loader v-if="isOptionLoading === true" />

            <article
              v-for="(item, index) in displayOptions"
              :key="index"
              class="media"
            >
              <figure class="media-left">
                <p class="image is-32x32">
                  <img
                    key="image"
                    :src="
                      item.OptionImage
                        | getLocationImage(item.LocationId, '160')
                    "
                    class="image"
                  />
                </p>
              </figure>
              <div class="media-content">
                <div class="content">
                  <span>{{ item.OptionName }}</span>
                </div>
              </div>
              <div class="media-right">
                <button
                  @click="addOption(item)"
                  class="button is-small is-primary"
                >
                  <span class="icon is-small">
                    <font-awesome-icon
                      :icon="['fas', 'plus']"
                      style="color: white;"
                    />
                  </span>
                </button>
              </div>
            </article>
          </div>
        </div>
        <div class="column is-three-fifths">
          <div class="tabs is-boxed">
            <ul>
              <li
                v-for="(space, index) in filteredSpaces"
                :key="index"
                @click="changeSpace(space.SpaceId)"
                :class="{ 'is-active': selectedSpace === space.SpaceId }"
              >
                <a>{{ space.SpaceName }} ({{ space.NrOfOptions }})</a>
              </li>
            </ul>
          </div>

          <Message
            v-if="filteredSpaceOptions.length === 0"
            :message="'No options selected'"
          />

          <table
            v-if="filteredSpaceOptions.length > 0"
            class="table is-fullwidth is-narrow"
          >
            <thead>
              <tr>
                <th>Name</th>
                <th width="100" class="has-text-right">Time</th>
                <th width="100" class="has-text-right">Qty.</th>
                <th width="100" class="has-text-right">Price</th>
                <th>
                  <span v-if="reservation.ReservationSetIds.length > 0"
                    >Change set</span
                  >
                </th>
                <th width="30">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(option, index) in filteredSpaceOptions" :key="index">
                <td>
                  {{ option.OptionName }}
                </td>
                <td>
                  <div v-if="option.TimeSelectable > 0" class="select is-small">
                    <select v-model="option.SelectedTime">
                      <option :value="-1" :selected="true">No time</option>
                      <option
                        v-for="timeSlot in timeSlots"
                        :key="timeSlot"
                        :value="timeSlot"
                        >{{ timeSlot | minutesToTime }}</option
                      >
                    </select>
                  </div>
                </td>
                <td>
                  <div class="field has-addons">
                    <p class="control">
                      <input
                        type="text"
                        v-model.number="option.Amount"
                        class="input is-small"
                      />
                    </p>
                    <p class="control">
                      <a class="button is-small is-static">X</a>
                    </p>
                  </div>
                </td>
                <td>
                  <div class="field has-addons">
                    <p class="control">
                      <a
                        class="button is-static is-small"
                        v-html="option.CurrencySymbol"
                      ></a>
                    </p>
                    <p class="control">
                      <InputNumeric
                        @change="updateCrcValue(option)"
                        @keyup="delay(updateCrcValue(option), 200)"
                        v-model.number="option.PricePerItem"
                        class="is-small"
                      />
                    </p>
                  </div>
                </td>
                <td>
                  <div
                    v-if="
                      reservation.ReservationSetIds.length > 0 &&
                        filteredSpaceOptions.length > 0
                    "
                  >
                    <div class="tabs is-toggle is-small">
                      <ul>
                        <li
                          :class="{
                            'is-active': option.ChangeAllInSet === true,
                          }"
                        >
                          <a @click="option.ChangeAllInSet = true">
                            <span>Yes</span>
                          </a>
                        </li>
                        <li
                          :class="{
                            'is-active': option.ChangeAllInSet === false,
                          }"
                        >
                          <a @click="option.ChangeAllInSet = false">
                            <span>No</span>
                          </a>
                        </li>
                      </ul>
                    </div>

                    <Message
                      v-if="changeAllInSet"
                      :message="'Change all in set'"
                    />
                  </div>
                </td>
                <td class="has-text-right">
                  <a @click="deleteOption(option)">
                    <span class="icon is-small has-text-danger">
                      <font-awesome-icon :icon="['fas', 'trash-alt']" />
                    </span>
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </ui-component-modal>
</template>

<script>
import { mapMutations, mapState } from 'vuex'
import { EventBus } from '@/eventbus/event-bus'
import reservationProvider from '@/providers/reservation'
import crcProvider from '@/providers/crc'
import optionProvider from '@/providers/option'
import InputNumeric from '../UI/Form/InputNumeric'

import Message from '@/components/UI/Message'

export default {
  components: {
    Message,
    InputNumeric,
  },

  props: {
    showModal: {
      type: Boolean,
      default: false,
    },
    onClickCancel: {
      type: Function,
      required: true,
    },
  },

  data() {
    return {
      changeAllInSet: false,
      isOptionLoading: false,
      isSavingError: false,
      isSaving: false,
      locationOptions: [],
      mReservation: null,
      newOptions: [],
      searchTerm: '',
      selectedCategory: 1,
      selectedSpace: 0,
    }
  },

  computed: {
    ...mapState('reservationStore', ['reservation']),

    filteredItems() {
      let result = []
      result = this.locationOptions.filter(
        (item) => item.CategoryId === this.selectedCategory
      )
      return result
    },

    filteredSpaces() {
      let spaces = [
        {
          SpaceId: 0,
          SpaceName: 'No space',
          NrOfOptions: this.newOptions.filter((o) => o.SpaceId === 0).length,
        },
      ]

      if (this.reservation.Spaces.length > 0) {
        for (let i = 0; i < this.reservation.Spaces.length; i++) {
          let resSpace = this.reservation.Spaces[i]
          spaces.push({
            SpaceId: resSpace.SpaceId,
            SpaceName: resSpace.SpaceName,
            NrOfOptions: this.newOptions.filter(
              (o) => o.SpaceId === resSpace.SpaceId
            ).length,
          })
        }
      }

      return spaces
    },

    filteredCategories() {
      let self = this
      let categories = []

      for (let i = 0; i < self.locationOptions.length; i++) {
        if (
          categories.findIndex(
            (c) => c.Id === self.locationOptions[i].CategoryId
          ) === -1
        ) {
          categories.push({
            Id: self.locationOptions[i].CategoryId,
            Name: self.locationOptions[i].CategoryName,
            NrOfOptions: self.locationOptions.filter(
              (o) => o.CategoryId === self.locationOptions[i].CategoryId
            ).length,
          })
        }
      }

      if (categories.length === 1) {
        self.selectedCategory = categories[0].Id
        self.changeCategory(self.selectedCategory)
      }

      return categories
    },

    displayOptions() {
      let result = []
      if (this.filteredItems.length > 0) {
        for (let i = 0; i < this.filteredItems.length; i++) {
          let optionIndex = this.newOptions.findIndex(
            (o) =>
              o.OptionId === this.filteredItems[i].OptionId &&
              o.SpaceId === this.selectedSpace
          )

          let reservationOptionIndex = this.reservation.Options.findIndex(
            (o) =>
              o.OptionId === this.filteredItems[i].OptionId &&
              o.SpaceId === this.selectedSpace
          )

          if (optionIndex === -1 && reservationOptionIndex === -1) {
            result.push(this.filteredItems[i])
          }
        }
      }
      return result
    },

    filteredSpaceOptions() {
      let options = []

      if (this.newOptions.length > 0) {
        options = this.newOptions.filter(
          (o) => o.SpaceId === this.selectedSpace
        )
      }

      return options
    },

    resStart() {
      let minStartMinutes = 0
      if (this.reservation && this.reservation.Spaces.length > 0) {
        var lowest = Number.POSITIVE_INFINITY
        var tmp
        for (var i = this.reservation.Spaces.length - 1; i >= 0; i--) {
          tmp = this.reservation.Spaces[i].StartMinutes
          if (tmp < lowest) {
            lowest = tmp
          }
        }
        minStartMinutes = lowest
      }
      return minStartMinutes
    },

    resEnd() {
      let maxEndMinutes = 0
      if (this.reservation && this.reservation.Spaces.length > 0) {
        var highest = Number.NEGATIVE_INFINITY
        var tmp
        for (var i = this.reservation.Spaces.length - 1; i >= 0; i--) {
          tmp = this.reservation.Spaces[i].EndMinutes
          if (tmp > highest) {
            highest = tmp
          }
        }
        maxEndMinutes = highest
      }
      return maxEndMinutes
    },

    timeSlots() {
      let slots = []
      let min = this.resStart
      let max = this.resEnd
      let minutes = 30

      let i = min
      while (i <= max) {
        slots.push(i)
        i = i + minutes
      }

      return slots
    },
  },

  created() {
    this.mReservation = JSON.parse(JSON.stringify(this.reservation))
    this.mReservation.Options = []
    this.getReservationOptions()
    //this.setSelectedSpace()
  },

  methods: {
    ...mapMutations('reservationStore', ['setReservation']),

    changeCategory(tab) {
      this.selectedCategory = tab
    },

    changeSpace(spaceId) {
      this.selectedSpace = spaceId
    },

    setSelectedSpace() {
      if (this.reservation.Spaces.length > 0) {
        this.selectedSpace = this.reservation.Spaces[0].SpaceId
      } else {
        this.selectedSpace = 0
      }
    },

    getCategoryName(value) {
      let name = ''

      switch (value) {
        case 1:
          name = 'F&B'
          break
        case 2:
          name = 'AV'
          break
        case 3:
          name = 'Parking'
          break
        case 4:
          name = 'Other'
          break
      }

      return name
    },

    getReservationOptions() {
      this.isOptionLoading = true

      optionProvider.methods
        .getReservationOptions(this.reservation.Id, 0, this.searchTerm)
        .then((response) => {
          if (response.status === 200) {
            this.locationOptions = response.data
          }
        })
        .finally(() => {
          this.isOptionLoading = false
        })
    },

    nrOfOptionsInCategory(categoryId) {
      return this.locationOptions.filter((o) => o.CategoryId === categoryId)
        .length
    },

    getNrOfSpaceOptions(spaceId) {
      let nrOfOptions = 0

      if (this.newOptions.length > 0) {
        nrOfOptions = this.newOptions.filter((o) => o.SpaceId === spaceId)
          .length
      }

      return nrOfOptions
    },

    addOption(item) {
      let qty = 1

      if (item.IsPP) {
        qty = this.reservation.TotalSeats

        if (this.selectedSpace > 0) {
          let space = this.reservation.Spaces.find(
            (o) => o.SpaceId === this.selectedSpace
          )
          if (space) {
            qty = space.Seats
          }
        }
      }

      let newOption = JSON.parse(JSON.stringify(item))
      newOption.SpaceId = this.selectedSpace
      newOption.ChangeAllInSet = false
      newOption.RequiredItem = 0
      newOption.Amount = qty
      this.newOptions.push(newOption)
    },

    deleteOption(option) {
      let optionIndex = this.newOptions.findIndex(
        (o) => o.OptionId === option.OptionId && o.SpaceId === option.SpaceId
      )
      if (optionIndex > -1) {
        this.newOptions.splice(optionIndex, 1)
      }
    },

    delay(callback, ms) {
      let self = this

      if (self.timer) {
        clearTimeout(self.timer)
      }
      self.timer = setTimeout(() => {
        callback.apply(self)
        clearTimeout(self.timer)
      }, ms || 0)
    },

    updateCrcValue(option) {
      let optionIndex = this.newOptions.findIndex(
        (o) => o.OptionId === option.OptionId && o.SpaceId === option.SpaceId
      )
      if (optionIndex > -1) {
        if (Number(option.PricePerItem) > 0) {
          crcProvider.methods
            .checkCrc(option.OptionId, option.PricePerItem)
            .then((response) => {
              if (response.status === 200) {
                //this.mOption.Crc = response.data
                this.newOptions[optionIndex].Crc = response.data
              }
            })
        }
      }
    },

    saveReservation() {
      let self = this

      if (!self.isSaving) {
        self.isSaving = true

        for (let i = 0; i < self.newOptions.length; i++) {
          let newOption = this.newOptions[i]

          self.mReservation.Options.push({
            ReservationId: Number(self.mReservation.Id),
            OptionId: newOption.OptionId,
            CategoryId: newOption.CategoryId,
            Quantity: newOption.Amount,
            CurrencyId: newOption.CurrencyId,
            PricePerItem: newOption.PricePerItem,
            PriceTotal: this.calculateTotalPrice(newOption),
            Crc: newOption.Crc,
            TaxId: newOption.TaxId,
            TaxPercentage: newOption.TaxPercentage,
            Id: 0,
            ChangeAllInSet: newOption.ChangeAllInSet,
            IsPP: newOption.IsPP,
            IsPerHour: newOption.IsPerHour,
            SelectedTime: newOption.SelectedTime,
            SpaceId: newOption.SpaceId,
          })
        }

        reservationProvider.methods
          .addReservationOptions(
            self.mReservation.Id,
            self.mReservation.Options
          )
          .then((response) => {
            if (response.status === 200) {
              // Update reservation store
              self.setReservation(response.data)

              EventBus.$emit('showToast', {
                type: 'info',
                message: 'Reservation data updated!',
              })

              this.onClickCancel()
            }
          })
          .catch((error) => {
            self.isSavingError = true
          })
          .finally(() => {
            self.isSaving = false
          })
      }
    },

    calculateTotalPrice(option) {
      let total = option.Amount * option.PricePerItem

      if (option.MaxTotal > 0 && total > option.MaxTotal) {
        total = option.MaxTotal
      }

      return total
    },
  },
}
</script>
